/**
 * Compliance → Objection - Phase 8 batch F item 1 (2026-05-17).
 *
 * GDPR Art 21 right to object. Fifth in the subject-rights run.
 *
 * Two paths, exposed in the UI:
 *   • Art 21(2) direct_marketing - the form sends the request and the
 *     queue row appears as `upheld` immediately. No DPO assessment.
 *   • Art 21(1) / 21(6) general grounds - pending → DPO clicks Assess →
 *     picks Uphold OR Override-with-compelling-grounds.
 *
 * The override path REQUIRES compelling-grounds text. The Assess dialog
 * shows different fields based on the picked outcome.
 */
import { useRegulationText } from '@/lib/regulation-terms';
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Plus,
  ShieldCheck,
  Eye,
  Ban,
  Scale,
  Megaphone,
  Gavel,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listObjectionJobs,
  createObjectionRequest,
  startObjectionAssessment,
  assessObjectionJob,
  type ObjectionJob,
  type ObjectionJobStatus,
  type ObjectionIdentifierKind,
  type ObjectionGround,
} from '@/lib/api';

const STATUS_VARIANT: Record<ObjectionJobStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  pending: 'glass',
  assessing: 'warning',
  upheld: 'success',
  overridden: 'warning',
  failed: 'critical',
  cancelled: 'critical',
};

const STATUS_ICON: Record<ObjectionJobStatus, typeof CheckCircle2> = {
  pending: Clock,
  assessing: Scale,
  upheld: CheckCircle2,
  overridden: Gavel,
  failed: XCircle,
  cancelled: Ban,
};

const IDENTIFIER_OPTIONS: ReadonlyArray<{ value: ObjectionIdentifierKind; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'aadhaar', label: 'Aadhaar' },
  { value: 'pan', label: 'PAN' },
  { value: 'customer_number', label: 'Customer number' },
  { value: 'custom', label: 'Custom identifier' },
];

const GROUND_OPTIONS: ReadonlyArray<{ value: ObjectionGround; label: string; hint: string; absolute: boolean }> = [
  {
    value: 'direct_marketing',
    label: 'Direct marketing (Art 21(2))',
    hint: 'ABSOLUTE - auto-upheld on create. Controller has no discretion.',
    absolute: true,
  },
  {
    value: 'legitimate_interests',
    label: 'Legitimate-interests processing (Art 21(1))',
    hint: 'DPO assesses. May override only with compelling legitimate grounds.',
    absolute: false,
  },
  {
    value: 'public_task',
    label: 'Public-task processing (Art 21(1))',
    hint: 'DPO assesses. May override only with compelling legitimate grounds.',
    absolute: false,
  },
  {
    value: 'scientific_research',
    label: 'Scientific / historical research (Art 21(6))',
    hint: 'DPO assesses. Override requires public-interest justification.',
    absolute: false,
  },
];

export function ComplianceObjection(): JSX.Element {
  const tx = useRegulationText();
  const [jobs, setJobs] = useState<ObjectionJob[]>([]);
  const [counts, setCounts] = useState({
    pending: 0, assessing: 0, upheld: 0, overridden: 0, failed: 0, cancelled: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [identifierKind, setIdentifierKind] = useState<ObjectionIdentifierKind>('email');
  const [principalIdentifier, setPrincipalIdentifier] = useState('');
  const [objectionGround, setObjectionGround] = useState<ObjectionGround>('direct_marketing');
  const [requestReason, setRequestReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [detailJob, setDetailJob] = useState<ObjectionJob | null>(null);

  // Assess dialog state
  const [assessTarget, setAssessTarget] = useState<ObjectionJob | null>(null);
  const [assessOutcome, setAssessOutcome] = useState<'upheld' | 'overridden'>('upheld');
  const [assessReason, setAssessReason] = useState('');
  const [compellingGrounds, setCompellingGrounds] = useState('');
  const [assessing, setAssessing] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listObjectionJobs({});
      setJobs(r.jobs);
      setCounts(r.byStatus);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load objection jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function handleSubmit(): Promise<void> {
    if (!principalIdentifier.trim()) {
      setError('Principal identifier is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createObjectionRequest({
        principalIdentifier: principalIdentifier.trim(),
        identifierKind,
        objectionGround,
        ...(requestReason.trim() ? { requestReason: requestReason.trim() } : {}),
      });
      setPrincipalIdentifier('');
      setRequestReason('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to submit objection');
    } finally {
      setSubmitting(false);
    }
  }

  function openAssessDialog(j: ObjectionJob): void {
    setAssessTarget(j);
    setAssessOutcome('upheld');
    setAssessReason('');
    setCompellingGrounds('');
  }

  async function handleAssess(): Promise<void> {
    if (!assessTarget) return;
    if (!assessReason.trim()) {
      setError('Assessment reason is required');
      return;
    }
    if (assessOutcome === 'overridden' && !compellingGrounds.trim()) {
      setError('Compelling-grounds text is required to override (Art 21(1) second sentence)');
      return;
    }
    setAssessing(true);
    setError(null);
    try {
      // If still pending, transition to assessing first (idempotent on
      // assessing, refused on direct_marketing or terminal states).
      if (assessTarget.status === 'pending') {
        try { await startObjectionAssessment(assessTarget.id); } catch { /* idempotent */ }
      }
      await assessObjectionJob(assessTarget.id, {
        outcome: assessOutcome,
        assessmentReason: assessReason.trim(),
        ...(assessOutcome === 'overridden' ? { compellingGroundsText: compellingGrounds.trim() } : {}),
      });
      setAssessTarget(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'assess failed');
    } finally {
      setAssessing(false);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Right to object (GDPR Art 21)"
        subtitle="Two paths: direct-marketing objections (Art 21(2)) auto-uphold on submit and cannot be overridden. General-grounds objections (Art 21(1)/(6)) require DPO assessment - uphold OR override-with-compelling-grounds."
        badges={[
          { label: 'DPO-gated assessment', variant: 'glass' },
          { label: 'Art 21(2) absolute for marketing', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Load error', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Objection action failed"
          detail={error}
          onRetry={() => setError(null)}
          retryLabel="Dismiss"
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <KpiCard label="Pending" value={counts.pending} icon={Clock} glow={counts.pending > 0 ? 'medium' : 'low'} hint="awaiting assessment" />
        <KpiCard label="Assessing" value={counts.assessing} icon={Scale} glow={counts.assessing > 0 ? 'medium' : 'low'} hint="DPO reviewing" />
        <KpiCard label="Upheld" value={counts.upheld} icon={CheckCircle2} glow={counts.upheld > 0 ? 'high' : 'low'} hint="processing paused" />
        <KpiCard label="Overridden" value={counts.overridden} icon={Gavel} glow={counts.overridden > 0 ? 'medium' : 'low'} hint="compelling grounds" />
        <KpiCard label="Failed" value={counts.failed} icon={XCircle} glow={counts.failed > 0 ? 'critical' : 'low'} hint="see error" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary" />
              Submit objection
            </CardTitle>
            <CardDescription>
              Identifier is SHA-256 hashed. Direct-marketing objections are auto-upheld on submit
              per Art 21(2); other grounds enter the DPO assessment queue.
            </CardDescription>
          </CardHeader>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-muted-foreground block mb-0.5">Identifier kind</label>
              <select
                value={identifierKind}
                onChange={(e) => setIdentifierKind(e.target.value as ObjectionIdentifierKind)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              >
                {IDENTIFIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Principal identifier</label>
              <input
                type="text"
                value={principalIdentifier}
                onChange={(e) => setPrincipalIdentifier(e.target.value)}
                placeholder="value"
                maxLength={200}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Objection ground</label>
              <select
                value={objectionGround}
                onChange={(e) => setObjectionGround(e.target.value as ObjectionGround)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              >
                {GROUND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {GROUND_OPTIONS.find((g) => g.value === objectionGround)?.hint}
              </div>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Reason (optional)</label>
              <textarea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Subject opted out of marketing via webform"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div className="flex justify-end pt-1">
              <Button size="sm" data-action="disc.comp_objection.a01" onClick={() => void handleSubmit()} disabled={submitting}>
                {submitting
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <Megaphone className="h-3.5 w-3.5 mr-1" />}
                Submit objection
              </Button>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Objection queue</CardTitle>
            <CardDescription>
              <strong>pending → assessing → upheld / overridden</strong>. Marketing objections skip
              straight to <strong>upheld</strong>. Each transition audit-chain logged.
            </CardDescription>
          </CardHeader>
          {loading && (
            <LoadingState caption="Loading…" />
          )}
          {!loading && jobs.length === 0 && (
            <div className="glass-lite rounded p-6 text-center text-xs text-muted-foreground">
              <Megaphone className="h-6 w-6 mx-auto mb-2 opacity-40" />
              <div>No objection jobs yet.</div>
            </div>
          )}
          {!loading && jobs.length > 0 && (
            <div className="space-y-2">
              {jobs.map((j, i) => {
                const SIcon = STATUS_ICON[j.status];
                const isMarketing = j.objectionGround === 'direct_marketing';
                return (
                  <motion.div
                    key={j.id}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                    className="glass-lite rounded p-3 text-xs"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="glass" className="text-[10px] font-mono">{j.identifierKind}</Badge>
                          <Badge variant={STATUS_VARIANT[j.status]} className="text-[10px] inline-flex items-center gap-1">
                            <SIcon className="h-3 w-3" />
                            {j.status}
                          </Badge>
                          <Badge variant="glass" className="text-[10px]">
                            {isMarketing ? 'marketing · Art 21(2)' : j.objectionGround.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate" title={j.principalIdentifierHash}>
                          {j.principalIdentifierHash.slice(0, 24)}…
                        </div>
                        {j.assessmentReason && (
                          <div className="text-[10px] mt-1 italic">"{j.assessmentReason}"</div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button size="sm" variant="glass" onClick={() => setDetailJob(j)} title="Details">
                          <Eye className="h-3 w-3" />
                        </Button>
                        {(j.status === 'pending' || j.status === 'assessing') && !isMarketing && (
                          <Button size="sm" variant="glass" onClick={() => openAssessDialog(j)} title="Assess">
                            <Scale className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </GlassCard>
      </div>

      {/* Assess dialog */}
      <Dialog open={assessTarget !== null} onOpenChange={(o) => { if (!o) setAssessTarget(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Assess objection</DialogTitle>
          </DialogHeader>
          {assessTarget && (
            <div className="space-y-3 text-xs">
              <div className="glass-lite rounded p-3">
                <div className="text-muted-foreground">Ground: {assessTarget.objectionGround.replace(/_/g, ' ')}</div>
                <div className="font-mono text-[10px] truncate" title={assessTarget.principalIdentifierHash}>
                  {assessTarget.principalIdentifierHash.slice(0, 24)}…
                </div>
                {assessTarget.requestReason && <div className="mt-1 italic">"{assessTarget.requestReason}"</div>}
              </div>
              <div>
                <label className="text-muted-foreground block mb-0.5">Outcome</label>
                <select
                  value={assessOutcome}
                  onChange={(e) => setAssessOutcome(e.target.value as 'upheld' | 'overridden')}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
                >
                  <option value="upheld">Uphold - pause processing</option>
                  <option value="overridden">Override - demonstrate compelling grounds</option>
                </select>
              </div>
              <div>
                <label className="text-muted-foreground block mb-0.5">Assessment reason</label>
                <textarea
                  value={assessReason}
                  onChange={(e) => setAssessReason(e.target.value)}
                  rows={2}
                  maxLength={1000}
                  className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
                />
              </div>
              {assessOutcome === 'overridden' && (
                <div>
                  <label className="text-muted-foreground block mb-0.5">
                    Compelling legitimate grounds (Art 21(1) second sentence) - REQUIRED
                  </label>
                  <textarea
                    value={compellingGrounds}
                    onChange={(e) => setCompellingGrounds(e.target.value)}
                    rows={4}
                    maxLength={4000}
                    placeholder={tx("Cite the legal basis under which the controller's interests override the data subject's rights, freedoms, and interests…")}
                    className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="glass" onClick={() => setAssessTarget(null)}>Cancel</Button>
                <Button size="sm" data-action="disc.comp_objection.a02 disc.comp_objection.a03" onClick={() => void handleAssess()} disabled={assessing}>
                  {assessing
                    ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    : assessOutcome === 'upheld' ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> : <Gavel className="h-3.5 w-3.5 mr-1" />}
                  {assessOutcome === 'upheld' ? 'Uphold objection' : 'Override with grounds'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail modal */}
      <Dialog open={detailJob !== null} onOpenChange={(o) => { if (!o) setDetailJob(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Objection job detail</DialogTitle>
          </DialogHeader>
          {detailJob && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Status</div>
                  <Badge variant={STATUS_VARIANT[detailJob.status]} className="text-[10px]">{detailJob.status}</Badge>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Ground</div>
                  <code className="font-mono">{detailJob.objectionGround}</code>
                </div>
                <div className="glass-lite rounded p-2 col-span-2">
                  <div className="text-muted-foreground mb-0.5">Principal hash</div>
                  <code className="font-mono text-[10px] break-all">{detailJob.principalIdentifierHash}</code>
                </div>
              </div>

              {detailJob.assessedByActorId && (
                <div className="glass-lite rounded p-3">
                  <div className="font-medium mb-1 flex items-center gap-1.5">
                    <Scale className="h-3.5 w-3.5" /> Assessment ({detailJob.assessmentOutcome})
                  </div>
                  <div className="text-muted-foreground">
                    By <code className="font-mono">{detailJob.assessedByActorId.slice(0, 8)}…</code>
                    {detailJob.assessedAt && <> at {new Date(detailJob.assessedAt).toLocaleString()}</>}
                  </div>
                  {detailJob.assessmentReason && (
                    <div className="mt-1">Reason: <em>{detailJob.assessmentReason}</em></div>
                  )}
                  {detailJob.compellingGroundsText && (
                    <div className="mt-2">
                      <div className="text-muted-foreground text-[10px] uppercase tracking-wider mb-1">
                        Compelling grounds (Art 21(1))
                      </div>
                      <div className="glass-lite rounded p-2 whitespace-pre-wrap">
                        {detailJob.compellingGroundsText}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                <div className="font-medium mb-1">Assets in scope ({detailJob.assetsScope.length})</div>
                {detailJob.assetsScope.length === 0 ? (
                  <div className="glass-lite rounded p-3 text-muted-foreground italic">
                    The access-graph walk found no assets touching this principal.
                  </div>
                ) : (
                  <div className="glass-lite rounded p-2 max-h-32 overflow-y-auto">
                    {detailJob.assetsScope.map((id) => (
                      <code key={id} className="block font-mono text-[10px]">{id}</code>
                    ))}
                  </div>
                )}
              </div>

              {detailJob.errorDetail && (
                <div className="glass-lite border border-sev-critical/40 rounded p-2 text-sev-critical">
                  <div className="font-medium">Error</div>
                  <div>{detailJob.errorDetail}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
